import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin, Building2, Stethoscope, Search, Phone, ShieldCheck, CalendarDays,
  Check, ArrowRight, ArrowLeft, Loader2, Star, PartyPopper, BadgeCheck, User,
  RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { formatTimeAmPm } from "../lib/timeUtils";
import { Label } from "../components/ui/label";
import SearchSelect from "../components/SearchSelect";
import StepProgress from "../components/StepProgress";
import SlotPicker from "../components/SlotPicker";
import {
  getStates, getCities, searchClinics, searchDoctors,
  lookupPatient, getSlotAvailability, getDoctorsByClinicId, confirmBooking,
  sendBookingOtp, verifyBookingOtp, resendBookingOtp,
  getDoctorPhoto,
} from "../lib/api";
import { normalizeSlotCollection } from "../lib/slotUtils";

const STEPS = ["Location", "Provider", "Doctor", "Details", "Slot", "Verify OTP", "Confirm"];
const today = new Date().toISOString().slice(0, 10);
const ease = [0.16, 1, 0.3, 1];

export default function BookAppointment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // step 0 — location
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");

  // step 1 — provider
  const [providerType, setProviderType] = useState("clinic");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [provider, setProvider] = useState(null);
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [selectedClinicDoctor, setSelectedClinicDoctor] = useState(null);
  const [loadingClinicDoctors, setLoadingClinicDoctors] = useState(false);
  const [doctorPhotoUrls, setDoctorPhotoUrls] = useState({});

  // step 2 — details
  const [phone, setPhone] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [existingPatients, setExistingPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [form, setForm] = useState({ name: "", age: "", gender: "" });

  // step 3 — slot
  const [date, setDate] = useState(today);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotLeave, setSlotLeave] = useState({ onLeave: false, leaveReason: "" });
  const [slot, setSlot] = useState("");
  const refreshLockRef = useRef(false);

  // step 4 — verify OTP (NEW)
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => { getStates().then(setStates); }, []);
  useEffect(() => {
    setCity("");
    if (state) getCities(state).then(setCities); else setCities([]);
  }, [state]);

  // fetch providers (debounced)
  const debRef = useRef(null);
  useEffect(() => {
    if (step !== 1 || !state || !city) return;
    setLoadingResults(true);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      const [clinicResults, doctorResults] = await Promise.allSettled([
        searchClinics(state, city, query),
        searchDoctors(state, city, query),
      ]);
      const clinics = clinicResults.status === "fulfilled" ? clinicResults.value : [];
      const doctors = doctorResults.status === "fulfilled" ? doctorResults.value : [];
      setResults([
        ...clinics.map((clinic) => ({ ...clinic, resultType: "clinic" })),
        ...doctors.map((doctor) => ({ ...doctor, resultType: "doctor" })),
      ]);
      setLoadingResults(false);
    }, 350);
    return () => clearTimeout(debRef.current);
  }, [step, query, state, city]);

  useEffect(() => {
    if (providerType !== "clinic" || !provider) {
      setClinicDoctors([]);
      setSelectedClinicDoctor(null);
      setLoadingClinicDoctors(false);
      return;
    }

    let active = true;
    setLoadingClinicDoctors(true);
    setClinicDoctors([]);
    setSelectedClinicDoctor(null);

    getDoctorsByClinicId(provider.id)
      .then((doctors) => {
        if (!active) return;
        const list = Array.isArray(doctors) ? doctors : [];
        setClinicDoctors(list);
      })
      .catch(() => {
        if (active) {
          setClinicDoctors([]);
          setSelectedClinicDoctor(null);
        }
      })
      .finally(() => {
        if (active) setLoadingClinicDoctors(false);
      });

    return () => { active = false; };
  }, [providerType, provider]);

  useEffect(() => {
    const doctors = [
      ...results.filter((result) => result.resultType === "doctor"),
      ...clinicDoctors,
    ];
    if (doctors.length === 0) {
      setDoctorPhotoUrls({});
      return undefined;
    }

    let active = true;
    const objectUrls = [];
    setDoctorPhotoUrls({});

    Promise.all(doctors.map(async (doctor) => {
      const doctorId = doctor.id ?? doctor.doctorId ?? doctor.doctor_id;
      const clinicId = doctor.clinicId ?? doctor.clinic_id ?? (clinicDoctors.includes(doctor) ? provider?.id : null);
      if (!doctorId || !clinicId) return [doctorId, ""];

      const photo = await getDoctorPhoto(doctorId, clinicId);
      if (!photo) return [doctorId, ""];
      const photoUrl = URL.createObjectURL(photo);
      objectUrls.push(photoUrl);
      return [doctorId, photoUrl];
    })).then((entries) => {
      if (active) setDoctorPhotoUrls(Object.fromEntries(entries));
      else objectUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    });

    return () => {
      active = false;
      objectUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    };
  }, [provider, clinicDoctors, results]);

  // fetch slots as soon as a doctor and date are available
  const refreshPatientSlots = async () => {
    const doctor = providerType === "doctor" ? provider : selectedClinicDoctor;
    if (!doctor) {
      setSlots([]);
      setSlotLeave({ onLeave: false, leaveReason: "" });
      return;
    }

    if (refreshLockRef.current) return;
    refreshLockRef.current = true;
    setLoadingSlots(true);

    try {
      const availability = await getSlotAvailability(doctor.id, providerType, date);
      setSlotLeave({ onLeave: availability.onLeave, leaveReason: availability.leaveReason });
      let nextSlots = availability.slots;
      const todayString = new Date().toISOString().slice(0, 10);
      if (date === todayString) {
        const now = new Date();
        nextSlots = nextSlots.filter((s) => {
          const timeRaw = (s.time ?? s.slotTime ?? "").toString();
          const hhmm = timeRaw.length >= 5 ? timeRaw.slice(0, 5) : timeRaw;
          const [hours, minutes] = hhmm.split(":").map((part) => parseInt(part, 10));
          if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return true;
          }
          const slotDate = new Date(date);
          slotDate.setHours(hours, minutes, 0, 0);
          return slotDate.getTime() > now.getTime();
        });
      }

      const freshSlots = normalizeSlotCollection(nextSlots);
      setSlots(freshSlots);

      if (slot && !freshSlots.some((s) => (s.time ?? s.slotTime ?? s) === slot || s.id === slot)) {
        setSlot("");
      }
    } catch {
      setSlots([]);
      setSlotLeave({ onLeave: false, leaveReason: "" });
    } finally {
      refreshLockRef.current = false;
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    const doctor = providerType === "doctor" ? provider : selectedClinicDoctor;
    if (!doctor) {
      setSlots([]);
      setSlotLeave({ onLeave: false, leaveReason: "" });
      setLoadingSlots(false);
      return;
    }

    refreshPatientSlots();
  }, [date, provider, providerType, selectedClinicDoctor]);

  useEffect(() => {
    const doctor = providerType === "doctor" ? provider : selectedClinicDoctor;
    if (!doctor) return;

    const refreshTimer = setInterval(() => {
      refreshPatientSlots();
    }, 20000);

    return () => clearInterval(refreshTimer);
  }, [date, provider, providerType, selectedClinicDoctor]);

  const phoneValid = phone.replace(/\D/g, "").length === 10;
  const detailsValid = verified && form.name.trim() && +form.age > 0 && form.gender;

  const selectExistingPatient = (patient) => {
    setSelectedPatient(patient);
    setForm({
      name: patient.patientName || "",
      age: String(patient.age || ""),
      gender: patient.gender || "",
    });
    setVerified(true);
    setIsReturning(true);
    toast.success(`Selected ${patient.patientName || "patient"}.`);
  };

  const addNewMember = () => {
    setSelectedPatient(null);
    setIsReturning(false);
    setForm({ name: "", age: "", gender: "" });
    setVerified(true);
    if (existingPatients.length > 0) {
      toast.message("Add a new member for this phone number.");
    }
  };

  const verify = async () => {
    if (!phoneValid) return;
    setVerifying(true);
    try {
      const res = await lookupPatient(phone);
      const patients = Array.isArray(res) ? res : [];
      const uniquePatients = Array.from(
        patients.reduce((map, patient) => {
          const key = patient.id ?? `${patient.patientName}-${patient.age}-${patient.gender}`;
          if (!map.has(key)) {
            map.set(key, patient);
          }
          return map;
        }, new Map())
        .values()
      );
      setExistingPatients(uniquePatients);

      if (uniquePatients.length > 0) {
        const firstPatient = uniquePatients[0];
        setSelectedPatient(firstPatient);
        setForm({
          name: firstPatient.patientName || "",
          age: String(firstPatient.age || ""),
          gender: firstPatient.gender || "",
        });
        setIsReturning(true);
        setVerified(true);
        toast.success(`Found ${uniquePatients.length} existing patient${uniquePatients.length > 1 ? "s" : ""}. Auto-selected the first one; you can still add a new member.`);
      } else {
        setSelectedPatient(null);
        setVerified(true);
        setIsReturning(false);
        setForm({ name: "", age: "", gender: "" });
        toast.message("No existing patient found. Add a new member.");
      }
    } catch (error) {
      setExistingPatients([]);
      setSelectedPatient(null);
      setVerified(false);
      setIsReturning(false);
      toast.error(error?.response?.data?.message || "Unable to check this phone number. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const sendOtpCode = async () => {
    setSendingOtp(true);
    try {
      const selectedDoctor = providerType === "doctor" ? provider : selectedClinicDoctor;
      const clinicId = providerType === "clinic" ? provider?.id : selectedDoctor?.clinicId;
      const res = await sendBookingOtp(phone, clinicId);
      if (res?.success) {
        setOtpSent(true);
        toast.success("OTP sent to your WhatsApp number.");
      } else {
        toast.error(res?.message || "Failed to send OTP.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtpCode = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a 6-digit OTP.");
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await verifyBookingOtp(phone, otp);
      if (res?.success) {
        setOtpVerified(true);
        toast.success("OTP verified! You can now confirm your booking.");
      } else {
        toast.error(res?.message || "Invalid OTP.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Verification failed. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  const resendOtpCode = async () => {
    setSendingOtp(true);
    try {
      const selectedDoctor = providerType === "doctor" ? provider : selectedClinicDoctor;
      const clinicId = providerType === "clinic" ? provider?.id : selectedDoctor?.clinicId;
      const res = await resendBookingOtp(phone, clinicId);
      if (res?.success) {
        setOtp("");
        toast.success("OTP resent to your WhatsApp number.");
      } else {
        toast.error(res?.message || "Failed to resend OTP.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to resend OTP. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const next = async () => {
    if (step === 1 && providerType === "doctor") {
      setStep(3);
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => {
    if (step === 3 && providerType === "doctor") {
      setStep(1);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const doctor = providerType === "doctor" ? provider : selectedClinicDoctor;
      if (!doctor?.id) {
        toast.error("Please select a doctor for this clinic before booking.");
        setSubmitting(false);
        return;
      }

      // Refresh slot availability right before booking to avoid race conditions
      try {
        const latestAvailability = await getSlotAvailability(doctor.id, providerType, date);
        setSlotLeave({ onLeave: latestAvailability.onLeave, leaveReason: latestAvailability.leaveReason });
        const latest = latestAvailability.slots;
        if (latestAvailability.onLeave) {
          setSlots([]);
          toast.error("The doctor is on leave for this date. Please choose another date.");
          setSubmitting(false);
          return;
        }
        const matched = latest.find(s => (s.time ?? s.slotTime ?? s) === slot || s.id === slot);
        if (!matched || (matched.blocked ?? matched.booked ?? matched.isBooked)) {
          // update UI slots and notify user
          setSlots(latest);
          toast.error("Selected slot is no longer available. Please choose another slot.");
          setSubmitting(false);
          return;
        }
      } catch (err) {
        // If slot check fails, proceed to attempt booking but warn in console
        console.warn("Slot availability check failed", err);
      }

      const payload = {
        doctorId: doctor.id,
        phone,
        patientName: form.name,
        age: +form.age,
        gender: form.gender,
        date,
        slot,
        scheduleId: doctor.scheduleId ?? selectedClinicDoctor?.scheduleId,
      };

      const bookingResponse = await confirmBooking(payload);
      const id = bookingResponse?.appointmentId ?? null;
      if (bookingResponse?.success && id) {
        setConfirmed({
          token: null,
          appointmentId: id,
          patientName: form.name,
          clinicName: providerType === "clinic" ? provider.name : provider.clinicName,
          location: `${city}, ${state}`,
          date,
          slot,
          whatsappSent: bookingResponse.whatsappSent === true,
        });
        if (bookingResponse.whatsappSent === true) {
          toast.success(bookingResponse.message || "Appointment confirmed. Confirmation sent via WhatsApp.");
        } else {
          toast.warning("Appointment confirmed. No separate WhatsApp confirmation will be sent. Please arrive at least 15 minutes before your appointment; your details are shown here.");
        }
      } else {
        const msg = bookingResponse?.message || "Booking failed. Please try again.";
        throw new Error(msg);
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg = error?.response?.data?.message || error?.response?.data || null;
      let message = serverMsg ? String(serverMsg) : "Booking failed. Please try again.";
      if (status === 409 && !serverMsg) {
        message = "Selected slot is already taken. Please choose another slot.";
      }
      if (status === 409) {
        try {
          const doctor = providerType === "doctor" ? provider : selectedClinicDoctor;
          if (doctor?.id) {
            const latestAvailability = await getSlotAvailability(doctor.id, providerType, date);
            setSlotLeave({ onLeave: latestAvailability.onLeave, leaveReason: latestAvailability.leaveReason });
            setSlots(latestAvailability.slots);
          }
        } catch (refreshError) {
          console.warn("Failed to refresh slots after conflict", refreshError);
        }
      }
      if (status) message += ` (${status})`;
      toast.error(message);
      console.error("Booking error:", { status, serverMsg, error });
    } finally {
      setSubmitting(false);
    }
  };


  const canNext =
    (step === 0 && state && city) ||
    (step === 1 && provider) ||
    (step === 2 && providerType === "clinic" && !loadingClinicDoctors && selectedClinicDoctor) ||
    (step === 3 && detailsValid) ||
    (step === 4 && slot) ||
    (step === 5 && otpVerified);

  const progressSteps = providerType === "clinic"
    ? STEPS
    : STEPS.filter((label) => label !== "Doctor");
  const progressCurrent = providerType === "doctor" && step > 1 ? step - 1 : step;

  if (confirmed) return <Success confirmed={confirmed} provider={provider} providerType={providerType} state={state} city={city} navigate={navigate} />;

  return (
    <div className="relative min-h-screen pt-28 pb-24">
      <div className="glow-teal pointer-events-none absolute top-24 left-1/2 -translate-x-1/2 h-[420px] w-[720px] blur-[120px] opacity-60" />
      <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-semibold">Book appointment</p>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-medium text-foreground">Five calm steps to care.</h1>
        </div>

        <StepProgress steps={progressSteps} current={progressCurrent} />

        <div className="mt-8 glass gradient-border rounded-3xl p-6 sm:p-9 min-h-[440px]">
          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease }}>

              {/* STEP 0 — LOCATION */}
              {step === 0 && (
                <div>
                  <StepHead icon={MapPin} title="Where are you located?" sub="Pick your state and city to find nearby care." />
                  <div className="mt-8 grid gap-5">
                    <div>
                      <Label className="text-muted-foreground">State / UT</Label>
                      <div className="mt-2">
                        <SearchSelect testId="select-state" icon={MapPin} value={state} onChange={setState}
                          options={states} placeholder="Select your state" searchPlaceholder="Search states…" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">City</Label>
                      <div className="mt-2">
                        <SearchSelect testId="select-city" icon={Building2} value={city} onChange={setCity}
                          options={cities} disabled={!state} placeholder={state ? "Select your city" : "Select a state first"} searchPlaceholder="Search cities…" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 1 — PROVIDER */}
              {step === 1 && (
                <div>
                  <StepHead icon={Stethoscope} title="Choose a clinic or doctor" sub={`Showing results in ${city}, ${state}.`} />
                  <div className="mt-6 flex justify-center">
                    <div className="inline-flex items-center gap-2 p-1 rounded-full bg-muted/60 border border-border">
                    {[['clinic', 'Clinics', Building2], ['doctor', 'Doctors', Stethoscope]].map(([val, lbl, Ic]) => (
                      <button key={val} data-testid={`tab-${val}`}
                        onClick={() => { setProviderType(val); setProvider(null); }}
                        className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                          providerType === val ? "aurora-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>
                        <Ic className="h-4 w-4" /> {lbl}
                      </button>
                    ))}
                    </div>
                  </div>

                  <div className="mt-5 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                    <Input data-testid="search-providers" value={query} onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search doctors, clinics, or specialities…"
                      className="pl-11 h-12 rounded-xl bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-ring/50" />
                  </div>

                  {(() => {
                    const visibleResults = query.trim()
                      ? results
                      : results.filter((result) => result.resultType === providerType);

                    return (
                  <div className="mt-5 space-y-3 max-h-[340px] overflow-y-auto pr-1">
                    {loadingResults ? (
                      Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-muted animate-pulse" />)
                    ) : visibleResults.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No matches. Try a different search.</div>
                    ) : visibleResults.map((r) => {
                      const resultType = r.resultType;
                      const isClinic = resultType === "clinic";
                      const sel = providerType === resultType && provider?.id === r.id;
                      const title = isClinic
                        ? (r.name || r.clinicName || r.clinic_name || "Clinic")
                        : (r.name || r.doctorName || r.doctor_name || "Doctor");
                      const mainMeta = isClinic
                        ? (r.specialities?.join(" · ") || r.speciality || r.specialization || "General")
                        : (r.specialization || r.speciality || "General");
                      const secondaryMeta = isClinic
                        ? (r.address || r.location || "Location unavailable")
                        : (r.clinicName || r.clinic_name || "Clinic unavailable");

                      return (
                        <button key={`${resultType}-${r.id}`} data-testid={`provider-${resultType}-${r.id}`} onClick={() => { setProviderType(resultType); setProvider(r); }}
                          className={`w-full rounded-2xl p-4 border transition-colors ${
                            sel ? "border-primary bg-primary/5 shadow-[0_0_0_1px_rgba(124,58,237,0.25)]" : "border-border bg-muted/40 hover:border-border"}`}>
                          <div className="flex items-start gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl border border-border/70 bg-slate-950/90 text-primary shadow-sm shadow-black/20">
                              {isClinic ? <Building2 className="h-6 w-6" /> : doctorPhotoUrls[r.id] ? (
                                <img src={doctorPhotoUrls[r.id]} alt="" className="h-full w-full rounded-3xl object-cover" />
                              ) : <Stethoscope className="h-6 w-6" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <h3 className="font-semibold text-foreground truncate">{title}</h3>
                                <span className="shrink-0 text-xs text-muted-foreground">{isClinic ? "Clinic" : "Doctor"}</span>
                              </div>
                              <p className="mt-2 text-sm text-muted-foreground leading-snug">{mainMeta}</p>
                              <p className="mt-2 text-xs text-muted-foreground/70 leading-snug">{secondaryMeta}</p>
                            </div>
                            <div className="flex items-start pt-1">
                              <span className={`h-4 w-4 rounded-full border-2 ${sel ? "border-primary bg-primary" : "border-border bg-transparent"}`} />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                    );
                  })()}
                </div>
              )}

              {/* STEP 2 — CLINIC DOCTOR */}
              {step === 2 && providerType === "clinic" && (
                <div>
                  <StepHead icon={Stethoscope} title="Choose a doctor" sub={`Select a doctor at ${provider?.name || "this clinic"}.`} />
                  <div className="mt-6 space-y-3">
                    {loadingClinicDoctors ? (
                      <div className="rounded-2xl border border-border bg-muted/40 p-6 text-center text-muted-foreground">Loading doctors…</div>
                    ) : clinicDoctors.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-center text-muted-foreground">
                        No doctors found for this clinic. Please go back and choose another clinic.
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {clinicDoctors.map((doc) => {
                          const selected = selectedClinicDoctor?.id === doc.id;
                          return (
                            <button key={doc.id} type="button" data-testid={`clinic-doctor-${doc.id}`} onClick={() => setSelectedClinicDoctor(doc)}
                              className={`w-full rounded-2xl border p-4 text-left transition-colors ${selected ? "border-primary bg-primary/5" : "border-border bg-muted/40 hover:border-primary"}`}>
                              <div className="flex items-start gap-4">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-3xl border border-border/70 bg-slate-950/90 text-primary shadow-sm shadow-black/20">
                                  {doctorPhotoUrls[doc.id] ? (
                                    <img src={doctorPhotoUrls[doc.id]} alt="" className="h-full w-full rounded-3xl object-cover" />
                                  ) : <User className="h-6 w-6" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground truncate">{doc.doctor_name || doc.doctorName || "Doctor"}</p>
                                  <p className="mt-2 text-sm text-muted-foreground leading-snug">{doc.specialization || doc.speciality || "General"}</p>
                                </div>
                                <span className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${selected ? "border-primary bg-primary" : "border-border bg-transparent"}`} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 3 — DETAILS */}
              {step === 3 && (
                <div>
                  <StepHead icon={Phone} title="Your details" sub="Enter your number — returning patients are recognised automatically." />
                  <div className="mt-8">
                    <Label className="text-muted-foreground">Mobile number</Label>
                    <div className="mt-2 flex gap-3">
                      <div className="relative flex-1">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">+91</span>
                        <Input data-testid="input-phone" value={phone}
                          onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setVerified(false); }}
                          placeholder="Enter your 10-digit mobile number" inputMode="numeric" maxLength={10}
                          inputMode="numeric"
                          className="pl-14 h-12 rounded-xl bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-ring/50" />
                      </div>
                      <button data-testid="verify-phone-btn" onClick={verify} disabled={!phoneValid || verifying}
                        className="aurora-gradient text-white font-semibold px-6 rounded-xl inline-flex items-center gap-2 disabled:opacity-40 transition-transform hover:scale-[1.02]">
                        {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />} Verify
                      </button>
                    </div>

                    {verified && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className={`mt-3 inline-flex items-center gap-2 text-sm rounded-full px-3 py-1 ${isReturning ? "bg-emerald-500/10 text-emerald-300" : "bg-primary/10 text-primary"}`}>
                        <ShieldCheck className="h-4 w-4" />
                        {isReturning ? "Returning patient — details pre-filled (editable)." : "New patient — verify by OTP to continue."}
                      </motion.div>
                    )}

                    <AnimatePresence>
                      {verified && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          className="overflow-hidden">
                          {existingPatients.length > 0 ? (
                            <div className="mt-6 rounded-3xl border border-border bg-muted/50 p-4">
                              <p className="text-sm text-muted-foreground mb-4">Existing members on this number</p>
                              <div className="grid gap-3">
                                {existingPatients.map((p) => (
                                  <button key={p.id || `${p.patientName}-${p.age}-${p.gender}`}
                                    type="button"
                                    onClick={() => selectExistingPatient(p)}
                                    className={`w-full rounded-2xl border p-4 text-left transition-colors ${selectedPatient?.id === p.id ? "border-primary bg-primary/10" : "border-border bg-muted/40 hover:border-primary"}`}>
                                    <div className="flex items-center justify-between gap-3">
                                      <div>
                                        <p className="font-medium text-foreground">{p.patientName || "Unnamed patient"}</p>
                                        <p className="text-sm text-muted-foreground">{[p.age ? `${p.age} yrs` : null, p.gender].filter(Boolean).join(" · ")}</p>
                                      </div>
                                      {selectedPatient?.id === p.id && <BadgeCheck className="h-4 w-4 text-primary" />}
                                    </div>
                                    <p className="mt-2 text-xs text-muted-foreground">
                                      {[p.age ? `${p.age} yrs` : null, p.gender].filter(Boolean).join(" · ")}
                                    </p>
                                  </button>
                                ))}
                                <button type="button" onClick={addNewMember}
                                  className="w-full rounded-2xl border border-dashed border-border bg-muted/40 py-3 text-sm font-semibold text-foreground hover:border-primary">
                                  + Add new member
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-6 rounded-2xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                              No existing patient found for this number. Add a new member below.
                            </div>
                          )}
                          <div className="mt-6 grid gap-5 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                              <Label className="text-muted-foreground">Full name</Label>
                              <Input data-testid="input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Enter your full name" className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground focus-visible:ring-ring/50" />
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Age</Label>
                              <Input data-testid="input-age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value.replace(/\D/g, "").slice(0, 3) })}
                                placeholder="Enter age in years" inputMode="numeric" className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground focus-visible:ring-ring/50" />
                            </div>
                            <div>
                              <Label className="text-muted-foreground">Gender</Label>
                              <div className="mt-2 grid grid-cols-3 gap-2">
                                {["Male", "Female", "Other"].map((g) => (
                                  <button key={g} type="button" data-testid={`gender-${g.toLowerCase()}`} onClick={() => setForm({ ...form, gender: g })}
                                    className={`h-12 rounded-xl border text-sm font-medium transition-colors ${form.gender === g ? "aurora-gradient text-white border-transparent" : "bg-muted/50 border-border text-muted-foreground hover:border-primary"}`}>
                                    {g}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* STEP 4 — SLOT */}
              {step === 4 && (
                <div>
                  <StepHead icon={CalendarDays} title="Pick a slot" sub={`With ${providerType === "clinic" ? selectedClinicDoctor?.doctor_name || selectedClinicDoctor?.doctorName || "your doctor" : provider?.name}`} />
                  <div className="mt-6 max-w-xs">
                    <div className="flex items-center justify-between gap-3">
                      <Label className="text-muted-foreground">Date</Label>
                    </div>
                    <Input data-testid="input-date" type="date" min={today} value={date}
                      onChange={(e) => { setDate(e.target.value); setSlot(""); }}
                      className="mt-2 h-12 rounded-xl bg-muted/50 border-border text-foreground dark:[color-scheme:dark] focus-visible:ring-ring/50" />
                  </div>
                  <div className="mt-6"><SlotPicker slots={slots} value={slot} onChange={setSlot} loading={loadingSlots} onLeave={slotLeave.onLeave} leaveReason={slotLeave.leaveReason} /></div>
                  <div className="mt-5 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-card border border-border" /> Available</span>
                    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded aurora-gradient" /> Selected</span>
                    <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-muted/40 border border-border" /> Booked</span>
                  </div>
                </div>
              )}

              {/* STEP 5 — VERIFY OTP */}
              {step === 5 && (
                <div>
                  <StepHead icon={ShieldCheck} title="Verify your phone" sub="We’ll send a 6-digit OTP to your WhatsApp number." />
                  <div className="mt-8">
                    {!otpSent ? (
                      <button
                        onClick={sendOtpCode}
                        disabled={sendingOtp}
                        className="w-full aurora-gradient text-white font-semibold px-7 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:scale-[1.02] transition-transform aurora-ring"
                      >
                        {sendingOtp ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending OTP...</> : <>Send OTP</>}
                      </button>
                    ) : (
                      <>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="otp-input" className="text-base font-medium mb-2 block">Enter OTP</Label>
                            <Input
                              id="otp-input"
                              type="text"
                              placeholder="000000"
                              maxLength="6"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                              className="text-center font-mono text-2xl tracking-widest"
                              disabled={otpVerified}
                            />
                          </div>
                          {!otpVerified ? (
                            <>
                              <button
                                onClick={verifyOtpCode}
                                disabled={otpVerifying || otp.length !== 6}
                                className="w-full aurora-gradient text-white font-semibold px-7 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:scale-[1.02] transition-transform aurora-ring"
                              >
                                {otpVerifying ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</> : <>Verify OTP <Check className="h-4 w-4" /></>}
                              </button>
                              <button
                                onClick={resendOtpCode}
                                disabled={sendingOtp}
                                className="w-full border border-border text-foreground font-semibold px-7 py-3 rounded-full inline-flex items-center justify-center gap-2 hover:bg-muted transition-colors"
                              >
                                {sendingOtp ? <><Loader2 className="h-4 w-4 animate-spin" /> Resending...</> : <>Resend OTP <RefreshCw className="h-4 w-4" /></>}
                              </button>
                            </>
                          ) : (
                            <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                              <BadgeCheck className="h-5 w-5 text-green-600" />
                              <span className="text-green-700 dark:text-green-300 font-medium">OTP verified successfully!</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 6 — CONFIRM */}
              {step === 6 && (
                <div>
                  <StepHead icon={Check} title="Review & confirm" sub="Please check everything before we lock it in." />
                  <div className="mt-8 rounded-2xl bg-muted/50 border border-border divide-y divide-border">
                    <Row label="Location" value={`${city}, ${state}`} />
                    <Row label={providerType === "clinic" ? "Clinic" : "Doctor"} value={provider?.name || provider?.doctorName || provider?.doctor_name} />
                    {providerType === "clinic" && <Row label="Doctor" value={selectedClinicDoctor?.doctor_name || selectedClinicDoctor?.doctorName} />}
                    <Row label={providerType === "clinic" ? "Speciality" : "Practises at"} value={providerType === "clinic" ? provider?.specialities?.join(", ") : provider?.clinicName} />
                    <Row label="Patient" value={`${form.name} · ${form.age} · ${form.gender}`} />
                    <Row label="Mobile" value={`+91 ${phone}`} />
                    <Row label="Date" value={date} />
                    <Row label="Time" value={formatTimeAmPm(slot)} highlight />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-9 flex items-center justify-between">
            <button onClick={back} disabled={step === 0} data-testid="step-back-btn"
              className="inline-flex items-center gap-2 text-muted-foreground font-medium disabled:opacity-0 hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 6 ? (
              <button data-testid="step-next-btn" onClick={next} disabled={!canNext}
                className="aurora-gradient text-white font-semibold px-7 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.02] transition-transform aurora-ring">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button data-testid="confirm-booking-btn" onClick={submit} disabled={submitting}
                className="aurora-gradient text-white font-semibold px-7 py-3 rounded-full inline-flex items-center gap-2 disabled:opacity-60 hover:scale-[1.02] transition-transform aurora-ring">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming…</> : <>Confirm booking <Check className="h-4 w-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepHead({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-2xl aurora-gradient grid place-items-center text-white shrink-0"><Icon className="h-5 w-5" /></div>
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-medium text-foreground">{title}</h2>
        <p className="mt-1 text-muted-foreground">{sub}</p>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between px-5 py-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-right font-medium ${highlight ? "aurora-text font-display text-xl" : "text-foreground"}`}>{value || "—"}</span>
    </div>
  );
}

function Success({ confirmed, provider, providerType, state, city, navigate }) {
  return (
    <div className="relative min-h-screen pt-32 pb-24">
      <div className="glow-teal pointer-events-none absolute top-24 left-1/2 -translate-x-1/2 h-[420px] w-[720px] blur-[120px] opacity-70" />
      <div className="relative mx-auto max-w-2xl px-5 sm:px-8 text-center">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 16 }}
          className="mx-auto h-20 w-20 rounded-full aurora-gradient grid place-items-center text-white">
          <PartyPopper className="h-9 w-9" />
        </motion.div>
        <h1 className="mt-8 font-display text-4xl sm:text-5xl font-medium text-foreground">You&apos;re all set.</h1>
        <p className="mt-4 text-muted-foreground text-lg">Your appointment is confirmed. Here are your details.</p>
        <p className={`mt-3 text-sm ${confirmed.whatsappSent ? "text-emerald-500" : "text-muted-foreground"}`}>
          {confirmed.whatsappSent
            ? "Appointment confirmation sent via WhatsApp."
            : "No separate WhatsApp confirmation will be sent. Please arrive at least 15 minutes before your appointment; your details are shown below."}
        </p>
        <div data-testid="booking-success" className="mt-10 glass rounded-3xl p-8 text-left">
          <div className="text-center pb-6 border-b border-border">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your token</p>
            <p className="mt-1 font-display text-6xl font-medium aurora-text">#{confirmed.token}</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5">
            <Field label="Appointment ID" value={confirmed.appointmentId || "N/A"} />
            <Field label="Patient" value={confirmed.patientName} />
            <Field label={providerType === "clinic" ? "Clinic" : "Doctor"} value={confirmed.clinicName || provider?.name} />
            <Field label="Location" value={confirmed.location || `${city}, ${state}`} />
            <Field label="Date" value={confirmed.date} />
            <Field label="Time" value={formatTimeAmPm(confirmed.slot)} />
          </div>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate("/queue")} data-testid="goto-queue-btn"
            className="aurora-gradient text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform aurora-ring">
            Track live queue <ArrowRight className="h-4 w-4" />
          </button>
          <button onClick={() => navigate("/find")}
            className="bg-muted border border-border text-foreground font-semibold px-7 py-3.5 rounded-full hover:bg-muted transition-colors">
            View my booking
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground/70">{label}</p>
      <p className="mt-1 font-medium text-foreground">{value || "—"}</p>
    </div>
  );
}
